import React from 'react';

import { CardHeaderProps } from '@mui/material';

import ContentCardFooter from './ContentCardFooter';
import ContentCardHeader from './ContentCardHeader';
import { CardContainer, Content, ContentProps, ScrollableContainer } from './index.styles';
import { HeadingVariant } from './types';
import { getPaddingEnum, getVariantEnum } from './utils';

export interface ContentCardProps {
  children: React.ReactNode;
  variant?: 'contained' | 'outlined' | 'paper' | 'dashed';
  padding?: 'none' | 'small' | 'medium' | 'large';
  maxWidth?: number;
  fillHeight?: boolean;
  title?: string | React.ReactNode;
  caption?: string | React.ReactNode;
  showHeaderDivider?: boolean;
  showFooterDivider?: boolean;
  headerActions?: React.ReactNode;
  footerActions?: React.ReactNode;
  backButtonHref?: string;
  titleVariant?: HeadingVariant;
  contentProps?: Partial<
    React.HTMLAttributes<HTMLDivElement> & Omit<ContentProps, '$headered' | '$footered'>
  >;
  headerProps?: Omit<CardHeaderProps, 'children'>;
}

const ContentCard = ({
  children,
  variant: variantProp,
  padding: paddingProp,
  maxWidth,
  title,
  caption,
  headerActions,
  footerActions,
  fillHeight = false,
  showHeaderDivider = false,
  showFooterDivider = false,
  backButtonHref,
  titleVariant = 'h6',
  contentProps,
  headerProps,
}: ContentCardProps) => {
  const variant = getVariantEnum(variantProp);
  const padding = getPaddingEnum(paddingProp);

  return (
    <CardContainer $variant={variant} $maxWidth={maxWidth} $fillHeight={fillHeight}>
      {(title || caption || headerActions || backButtonHref) && (
        <ContentCardHeader
          title={title}
          titleVariant={titleVariant}
          caption={caption}
          showDivider={showHeaderDivider}
          action={headerActions}
          backButtonHref={backButtonHref}
          padding={padding}
          variant={variant}
          {...headerProps}
        />
      )}
      <ScrollableContainer $fillHeight={fillHeight}>
        <Content
          $headered={!!title || !!headerActions}
          $footered={!!footerActions}
          $fillHeight={fillHeight}
          $padding={padding}
          $variant={variant}
          {...contentProps}
        >
          {children}
        </Content>
      </ScrollableContainer>
      {footerActions && (
        <ContentCardFooter
          showDivider={showFooterDivider}
          actions={footerActions}
          padding={padding}
          variant={variant}
        />
      )}
    </CardContainer>
  );
};

export default ContentCard;
